import * as React from 'react';

import type { Atoms, PrimitivePropsWithRef } from '@rtkwlf/fenrir-react';
import { Primitive } from '@rtkwlf/fenrir-react';

import { DataError } from '@packages/components/error/DataError';
import { Error } from '@packages/components/error/Error';
import { Loader } from '@packages/components/loader/loader';
import { NoData } from '@packages/components/widgets/NoData';

export interface GeneralWrapperProps
  extends React.ComponentPropsWithoutRef<typeof Primitive.div> {
  isNoData?: boolean | undefined;
  isLoading?: boolean | undefined;
  loader?: React.ReactElement;
  error?: React.ReactElement;
  noData?: React.ReactElement;
  isFetching?: boolean | undefined;
  isError?: boolean | undefined;
  isDataError?: boolean | undefined;
  noDataMessage?: string;
  withNoDataIcon?: boolean;
  height?: Atoms['height'];
  fetchingWithMask?: boolean;
  justifyContent?: 'flex-start' | 'center';
}

const CenterWrapper: React.FC<PrimitivePropsWithRef<'div'>> = (props) => (
  <Primitive.div
    {...props}
    position="relative"
    width="full"
    display="flex"
    justifyContent="center"
    alignItems="center"
    alignSelf="center"
    zIndex={0}
  />
);

export const GeneralWrapper = ({
  isLoading = false,
  isFetching = false,
  isError = false,
  isNoData = false,
  isDataError = false,
  loader,
  error = <Error />,
  noData,
  children,
  noDataMessage,
  withNoDataIcon,
  height = 'full',
  fetchingWithMask = true,
  justifyContent,
  ...rest
}: React.PropsWithChildren<GeneralWrapperProps>) => {
  if (!isFetching && (isLoading || isNoData || isError)) {
    if (isError) {
      return (
        <CenterWrapper height={height}>
          {isDataError ? <DataError /> : error}
        </CenterWrapper>
      );
    }

    if (isLoading) {
      return (
        <CenterWrapper height={height}>{loader || <Loader />}</CenterWrapper>
      );
    }
    return (
      noData ?? (
        <CenterWrapper height={height}>
          <NoData message={noDataMessage} withIcon={withNoDataIcon} />
        </CenterWrapper>
      )
    );
  }
  return (
    <Primitive.div position="relative" width="full" {...rest}>
      {children}
      {isFetching ? (
        <Loader withMask={fetchingWithMask} justifyContent={justifyContent} />
      ) : undefined}
    </Primitive.div>
  );
};
